import type { TimeValue } from '@/components/ui/time-picker'
import type { Claim, ReporterRelationship } from '../../../../models/claim'

/** The entire Step 1 wizard form — the single source of truth (react-hook-form). */
export interface Step1FormValues {
  // Reporter
  name: string
  phone: string
  relationships: ReporterRelationship[]
  // Photos / documents selected in the dropzone (held in memory until the draft exists)
  photos: File[]
  /** Earliest EXIF capture time of the added photos — a fallback for Step 3's date and time. */
  capturedAt: Date | null
  photoDeferred: boolean
  deferralReason: string
}

export const STEP1_DEFAULTS: Step1FormValues = {
  name: '',
  phone: '',
  relationships: [],
  photos: [],
  capturedAt: null,
  photoDeferred: false,
  deferralReason: '',
}

/** Parse a backend "HH:MM[:SS]" time string into a TimeValue (null when absent). */
export function parseTimeValue(time: string | null | undefined): TimeValue | null {
  if (!time) return null
  const [h, m] = time.split(':')
  const hours = Number(h)
  const minutes = Number(m)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
  return { hours, minutes }
}

/** Build form defaults from an existing claim (edit mode). Photos start empty —
 * already-uploaded files live on the backend and aren't re-hydrated as Files. */
export function claimToStep1Defaults(claim: Claim): Step1FormValues {
  return {
    ...STEP1_DEFAULTS,
    name: claim.reporter?.name ?? '',
    phone: claim.reporter?.phone ?? '',
    relationships: claim.reporter?.relationships ?? [],
    photoDeferred: claim.photoUploadDeferred,
    deferralReason: claim.photoDeferralReason,
  }
}

/** Build form defaults for a brand-new claim, prefilling the reporter. */
export function newClaimDefaults(reporterName = '', reporterPhone = ''): Step1FormValues {
  return { ...STEP1_DEFAULTS, name: reporterName, phone: reporterPhone }
}
